// ADR 036 V2 — entity reference forms.
//
// Read verbs (clone / info / rollback / pull) accept three input forms:
//
//   - a canonical UUID                       → globally addressable; the
//     server resolves it in any org the caller is a member of.
//   - `<org-slug>/<entity-slug>` (GitHub-style) → org-qualified; the server
//     resolves it inside the named org. Sent as ONE encoded path segment.
//   - a bare slug                            → legacy; resolves in the
//     active org via the X-CloudGrid-Org header, as before V2.
//
// For the first two ("global refs") the CLI skips `getActiveOrg` entirely —
// no `/api/v2/grids` round-trip, no "No active org" error, no need to flip
// the active org to reach an entity in another of the caller's orgs.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** True when the input is a canonical entity UUID. */
export function isEntityUuid(input: string): boolean {
  return UUID_RE.test(input);
}

/**
 * Parse the `<org-slug>/<entity-slug>` form. Returns null unless the input
 * has exactly one `/` and both segments are slug-shaped (lowercase
 * alphanumeric + dashes, no leading/trailing dash).
 */
export function parseOrgSlugRef(input: string): { orgSlug: string; entitySlug: string } | null {
  const parts = input.split('/');
  if (parts.length !== 2) return null;
  const [orgSlug, entitySlug] = parts;
  if (!SLUG_RE.test(orgSlug) || !SLUG_RE.test(entitySlug)) return null;
  return { orgSlug, entitySlug };
}

/**
 * True when the input addresses an entity without needing the active org:
 * a UUID or an `org/slug` ref. Bare slugs return false — they still resolve
 * against the active org server-side.
 */
export function isGlobalEntityRef(input: string): boolean {
  return isEntityUuid(input) || parseOrgSlugRef(input) !== null;
}

/**
 * True when a persisted link.json `entity_id` looks like a slug rather than a
 * server entity id. Server-issued entity ids are UUIDs (`randomUUID`); the
 * pre-fix `plug --existing` bug wrote the server-canonical *slug* (e.g.
 * `dino-store-0b8b`) into `entity_id`, which makes deploy-by-id 404. Detect
 * that corruption specifically: a value that is NOT a UUID but IS slug-shaped.
 * (A synthetic non-UUID/non-slug id — e.g. an `e_`-prefixed test fixture — is
 * left alone: it is not the observed failure mode.)
 */
export function linkEntityIdLooksLikeSlug(entityId: string): boolean {
  if (isEntityUuid(entityId)) return false;
  return SLUG_RE.test(entityId);
}
